<script setup>
defineProps({
  student: { type: Object, required: true },
  visits: { type: Array, required: true },
  removeStudent: { type: Function, required: true },
  editStudent: { type: Function, required: true },
  deleteVisit: { type: Function, required: true },
  editVisit: { type: Function, required: true },
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const dateOnlyFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
});

function formatDate(isoString) {
  return dateFormatter.format(new Date(isoString));
}

// Absences are logged for the whole day, so the visit's time isn't meaningful.
function formatVisitWhen(visit) {
  return visit.subject === 'Absent'
    ? dateOnlyFormatter.format(new Date(visit.seen_at))
    : formatDate(visit.seen_at);
}
</script>

<template>
  <wa-card>
    <div slot="header" class="header">
      <strong>{{ student.name }}</strong>
      <span class="row-actions student-actions">
        <wa-button size="s" appearance="outlined" @click="editStudent(student)"
          >Edit</wa-button
        >
        <wa-button
          size="s"
          appearance="outlined"
          variant="danger"
          @click="removeStudent(student.id)"
        >
          Delete
        </wa-button>
      </span>
    </div>

    <p class="last-seen">
      <template v-if="visits.length"
        >Last seen: {{ formatDate(visits[0].seen_at) }}</template
      >
      <template v-else>No visits logged yet</template>
    </p>

    <wa-details v-if="visits.length" summary="History">
      <ul class="history">
        <li v-for="visit in visits" :key="visit.id">
          <span class="when">
            {{ formatVisitWhen(visit) }}
            <template v-if="visit.subject || visit.duration_minutes">
              ·
              {{
                [
                  visit.subject,
                  visit.duration_minutes && `${visit.duration_minutes} min`,
                ]
                  .filter(Boolean)
                  .join(', ')
              }}
            </template>
          </span>
          <span v-if="visit.notes" class="notes">{{ visit.notes }}</span>
          <span class="row-actions visit-actions">
            <wa-button size="s" appearance="plain" @click="editVisit(visit)"
              >Edit</wa-button
            >
            <wa-button
              size="s"
              appearance="plain"
              variant="danger"
              @click="deleteVisit(visit.id, student.name)"
            >
              Remove
            </wa-button>
          </span>
        </li>
      </ul>
    </wa-details>
  </wa-card>
</template>

<style scoped>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--wa-space-s);
}

.last-seen {
  color: var(--wa-color-text-quiet);
}

.history {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--wa-space-s);
}

.history li {
  display: flex;
  flex-direction: column;
  gap: var(--wa-space-2xs);
}

.history li + li {
  border-top: solid 1px var(--wa-color-surface-border);
  padding-top: var(--wa-space-s);
}

.row-actions {
  display: flex;
  gap: var(--wa-space-2xs);
  justify-content: flex-end;
}

.visit-actions {
  font-size: var(--wa-font-size-xs);
}

@media print {
  .row-actions {
    display: none;
  }
}

.history .when {
  font-weight: 600;
}

.history .notes {
  display: block;
  color: var(--wa-color-text-quiet);
  white-space: pre-wrap;
}
</style>
